import { unmaskString } from "./mask";
import { LocaleMapping } from "@web-monorepo/translation-runtime";
import { apiFetch } from "@web-monorepo/fetchers";
import { captureException } from "@web-monorepo/telemetry";
import type { ReadCallback } from "i18next";
import type { JsonObject } from "type-fest";

// Sentinel value passed to i18next callback to indicate error was already captured
export const BACKEND_SENTINEL = "__BACKEND_REPORTED__";

// Hardcoded allowlist patterns for translation file pathnames (SSRF prevention). Matched
// against the *resolved* pathname (see Backend.read, which separately checks the resolved
// origin), which may carry an arbitrary deploy path prefix (PR preview subpaths can contain
// characters outside \w — e.g. "." in a version string — so the prefix allows word characters
// plus ".", "~", and "-"). Only the filename is attacker-adjacent in practice (it comes from
// the build-generated LocaleMapping), so only the filename is tightly constrained.
const ALLOWED_TRANSLATION_PATHNAME_PATTERNS: readonly RegExp[] = [
  // Production: .../assets/locale-{lang}-{namespace}-{hash}.json, behind any path prefix
  /^\/(?:[\w.~-]+\/)*assets\/locale-[\w-]+-[\w-]+-[a-f0-9]+\.json$/,
  // Development: /virtual:strings-{lang}.json
  /^\/virtual:strings-[\w-]+\.json$/,
] as const;

function isAllowedTranslationPathname(pathname: string): boolean {
  return ALLOWED_TRANSLATION_PATHNAME_PATTERNS.some((pattern) => pattern.test(pathname));
}

// Captured once at module load — before any in-app navigation — so a relative locale path
// always resolves against the deploy's own root, regardless of what the SPA's router does to
// the document URL afterward (e.g. a browser-history router pushing a deep route). Every app
// that can ever emit a relative locale path (Vite base "") happens to route with a hash router
// today, which would make a per-call `document.baseURI` read safe too — capturing eagerly
// removes the dependency on that holding forever.
const DOCUMENT_BASE = document.baseURI;

export const Backend = {
  type: "backend" as const,
  init() {},
  // eslint-disable-next-line complexity -- pre-existing violation
  async read(lng: string, ns: string, callback: ReadCallback) {
    try {
      if (lng === "en" || lng === "en-US") {
        callback(null, {});
        return;
      }

      const file = LocaleMapping[lng]?.[ns];

      if (!file) {
        callback(null, {});
        return;
      }

      // Resolve against the captured document base, not window.location.origin, so a relative
      // path (emitted whenever the Vite base is "" — admin and community always; teach/home/
      // student on PR preview deploys; toolkit if its prSubpath config ever goes empty) lands
      // in the deploy's own subpath instead of the origin root. An absolute path (production
      // teach/home/student, and toolkit today) resolves identically against any base, so
      // production is unaffected.
      const resolvedUrl = new URL(file, DOCUMENT_BASE);

      // Validate the URL we're actually about to fetch (SSRF prevention). Origin first: a
      // cross-origin resolution should always report as a cross-origin rejection, even if the
      // pathname happens to look plausible. Then the pathname shape — plus a query string or
      // fragment, which a mapping value could carry (e.g. "assets/locale-....json?x=1") even
      // though apiFetch only ever forwards resolvedUrl.pathname.
      if (resolvedUrl.origin !== window.location.origin) {
        callback(new Error("URL does not resolve to the current origin"), null);
        return;
      }
      if (!isAllowedTranslationPathname(resolvedUrl.pathname) || resolvedUrl.search || resolvedUrl.hash) {
        callback(new Error("URL does not match allowed translation file patterns"), null);
        return;
      }

      const response = await apiFetch<JsonObject | string>({
        method: "GET",
        path: resolvedUrl.pathname,
        credentials: "omit",
      });

      if (!response.success) {
        throw response.error;
      }

      const contentType = response.headers["content-type"];
      const isValidContentType = contentType?.startsWith("application/json") || contentType?.startsWith("text/plain");

      if (!isValidContentType) {
        throw new Error(`Invalid content type: ${contentType}`);
      }

      let body: JsonObject;
      if (typeof response.data === "string") {
        try {
          body = JSON.parse(response.data) as JsonObject;
        } catch (error) {
          const parseError = new Error("Failed to parse translations JSON");
          parseError.cause = error;
          throw parseError;
        }
      } else {
        body = response.data;
      }
      for (const str of Object.values(body)) {
        if (typeof str !== "string") {
          continue;
        }

        unmaskString(str);
      }
      callback(null, body);
      // eslint-disable-next-line no-catch-all/no-catch-all -- pre-existing violation
    } catch (error) {
      // Capture the actual error with proper fingerprinting/context
      captureException(error);
      // Pass sentinel so i18n.ts knows not to double-report
      callback(BACKEND_SENTINEL, null);
    }
  },
};
